import DeleteIcon from "@assets/icons/delete.svg?react";
import _ from "lodash";
import { CourseTabComponentProps } from "@vixi/types";
import { Controller, Path, useFieldArray, useForm, UseFormReturn } from "react-hook-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "@vixi/localization";
import { CourseData } from "@vixi/types";
import { CourseStepPaddingWrapper, EditableBox, CourseStepDivider, RadioCheck } from "..";
import { CourseTabTitle } from "../../CourseTabs/CourseTabTitle";
import { useCourseEditorContext } from "../../context";
import { Button } from "@/components/base/button";
import { TransparentTextArea } from "@/components/input";
import { cn } from "@/lib/utils";
import useUiStore from "@/store/ui.state";

export interface MultipleChoiceStepProps extends CourseTabComponentProps, CourseData.McQuestion {}

export interface MultipleChoiceQuestionFormValues extends Omit<CourseData.McQuestion, "options"> {
  options: { value: string }[]; // Answers are stored as an array of objects due to react-hook-form's limitations
}

export function MultipleChoiceStep({ options: storeAnswers, ...props }: MultipleChoiceStepProps) {
  const { t } = useTranslation();
  const { setValue } = useCourseEditorContext();
  const path = useMemo(() => props.path.concat("multipleChoice"), [props.path]);
  const { mode } = useUiStore((state) => state.courseEditor);
  const isDisabled = mode !== "edit";

  const multipleChoiceQuestionForm = useForm<MultipleChoiceQuestionFormValues>({
    defaultValues: {
      question: props.question,
      options: storeAnswers.map((answer) => ({ value: answer })),
      correctAnswer: props.correctAnswer,
    },
  });

  const { watch, register, getValues } = multipleChoiceQuestionForm;
  const { append: appendAnswer, remove: removeAnswer } = useFieldArray({
    name: "options",
    control: multipleChoiceQuestionForm.control,
  });

  const addAnswer = () => {
    appendAnswer({ value: "" });
  };

  // Update the global state with the current form values
  const updateState = useCallback(() => {
    const setValueOptions = {
      condition: (_prev?: CourseData.McQuestion, parent?: CourseData.McqStep) =>
        parent?.id === props.id,
    };

    setValue(
      path,
      {
        ...getValues(),
        options: getValues("options").map((answer) => answer.value),
      },
      setValueOptions,
    );
  }, [path, getValues]);
  const debouncedUpdate = useMemo(() => _.debounce(updateState, 500), [updateState]);

  useEffect(() => {
    return updateState;
  }, []);
  watch(async () => debouncedUpdate());

  const answers = watch("options");
  const correctAnswer = watch("correctAnswer");

  return (
    <>
      <CourseTabTitle className="mx-auto w-fit">{t("Question.Mcq")}</CourseTabTitle>

      <CourseStepPaddingWrapper>
        <EditableBox>
          <TransparentTextArea
            className="fs-20"
            minRows={2}
            maxRows={5}
            disabled={isDisabled}
            {...register("question", { shouldUnregister: false })}
          />
        </EditableBox>
      </CourseStepPaddingWrapper>

      <CourseStepDivider />

      <CourseStepPaddingWrapper>
        <div className="flex max-h-full flex-col gap-5 overflow-auto">
          {answers.map((answer, i) => (
            <McAnswer
              key={i}
              index={i}
              fieldPath={`options.${i}.value`}
              text={answer.value}
              form={multipleChoiceQuestionForm}
              showDelete={answers.length > 3}
              onDelete={removeAnswer}
              correct={correctAnswer === i}
            />
          ))}
        </div>

        {answers.length < 3 && (
          <Button variant="outlined" size="pill" className="mt-5 self-start" onClick={addAnswer}>
            {t("Question.AddAnswer")}
          </Button>
        )}
      </CourseStepPaddingWrapper>
    </>
  );
}

interface BaseMcQuestion {
  correctAnswer: any;
}

export const mcAnswerClasses = {
  root: "McAnswer-root",
  checkMark: "McAnswer-checkMark",
  checkMarkState: (state: boolean) => `McAnswer-checkMark-${state ? "correct" : "incorrect"}`,
};

export interface McAnswerProps<T extends BaseMcQuestion> {
  text: string;
  fieldPath?: Path<T>;
  correctAnswerPath?: Path<T>;
  index?: number;
  correct: boolean;
  disableText?: boolean;
  showDelete?: boolean;
  form: UseFormReturn<T>;
  onDelete?: (index?: number) => void;
}

export function McAnswer<T extends BaseMcQuestion>({
  index,
  fieldPath,
  correctAnswerPath,
  form,
  onDelete,
  ...props
}: McAnswerProps<T>) {
  const { register } = form;
  const { mode } = useUiStore((state) => state.courseEditor);
  const isDisabled = mode !== "edit";

  return (
    <div className={cn(mcAnswerClasses.root, "flex items-center gap-1.25")}>
      <EditableBox
        className={cn(
          "mt-0 flex flex-1 items-center pe-1.25",
          props.correct ? "bg-success-light" : "bg-destructive-200",
        )}
      >
        <TransparentTextArea
          className="fs-20 -me-11.25 min-w-0 pe-12.5"
          minRows={1}
          maxRows={3}
          disabled={props.disableText || isDisabled}
          {...(fieldPath ? register(fieldPath) : {})}
        />
        <Controller
          control={form.control}
          name={correctAnswerPath || ("correctAnswer" as Path<T>)}
          render={({ field: { onChange, ...field } }) => (
            <RadioCheck
              className={`${mcAnswerClasses.checkMark} ${mcAnswerClasses.checkMarkState(props.correct)} mx-auto -mb-0.5`}
              style={{ pointerEvents: isDisabled ? "none" : "auto" }}
              checked={props.correct}
              {...field}
              value={index}
              onChange={() => onChange(index)}
            />
          )}
        />
      </EditableBox>

      {(props.showDelete ?? true) && (
        <Button
          size="icon"
          variant="light"
          color="foreground"
          onClick={onDelete && (() => onDelete(index))}
        >
          <DeleteIcon />
        </Button>
      )}
    </div>
  );
}

export default MultipleChoiceStep;
